import axios, { AxiosError } from 'axios';
import {
  Analytics,
  BreakoutStockItem,
  IndexHistory,
  IndexHistoryEntry,
  IndexLatestQuote,
  SectorSummary,
  SectorUniverse,
  StockHistory,
  CorporateFeedItem,
  FeedRunMetadata,
  CatalystsSummaryIndex,
  ThirtyDayPriceMover,
  VolumeAboveAverageStreak,
  VolumeShocker,
  EquityDeliveryRecord,
  CalculatorStatus,
  AccumulationCountPayload,
  StockSessionMetric,
  SectorStockSignal,
} from './types';
import { ApiError, retryWithBackoff } from './utils/apiErrors';
import { API_CONFIG } from './constants';
import { getAuthToken } from './services/authService';
import { toIndexFileSafeName } from './utils/dataUtils';
import { decryptJsonResponse } from './utils/cryptoUtils';
import sectorUniverseData from './constants/sectorUniverseData.json';
import { ensureSnapshotLoaded } from './services/stockSnapshotStore';
import {
  mergeSectorNumberMap,
  mergeSectorStringArrayMap,
  mergeSectorSignalsMap,
  mergeNestedSectorMap,
} from './utils/sectorFilter';

const calculatorStatusUrl = `${API_CONFIG.BASE_URL}${API_CONFIG.ENDPOINTS.CALCULATOR_STATUS}`;
const analyticsDataUrl = `${API_CONFIG.BASE_URL}${API_CONFIG.ENDPOINTS.ANALYTICS}`;
const analyticsSummaryUrl = `${API_CONFIG.BASE_URL}${API_CONFIG.ENDPOINTS.ANALYTICS_SUMMARY}`;
const analyticsSummaryArchiveUrl = `${API_CONFIG.BASE_URL}${API_CONFIG.ENDPOINTS.ANALYTICS_SUMMARY_ARCHIVE}`;
const sectorSummaryUrl = `${API_CONFIG.BASE_URL}${API_CONFIG.ENDPOINTS.SECTOR_SUMMARY}`;
const sectorSummaryArchiveUrl = `${API_CONFIG.BASE_URL}${API_CONFIG.ENDPOINTS.SECTOR_SUMMARY_ARCHIVE}`;
const sectorUniverseUrl = `${API_CONFIG.BASE_URL}${API_CONFIG.ENDPOINTS.SECTOR_UNIVERSE}`;
const allStocksDataUrl = `${API_CONFIG.BASE_URL}${API_CONFIG.ENDPOINTS.ALL_STOCKS}`;
const indexLatestUrl = `${API_CONFIG.BASE_URL}${API_CONFIG.ENDPOINTS.INDEX_LATEST}`;
const ANALYTICS_STORAGE_KEY = 'niftysutra.analytics-summary-cache';
const MAX_PERSISTED_ANALYTICS_BYTES = 2_500_000;

let analyticsCache: Analytics[] | null = null;
let analyticsRequestPromise: Promise<Analytics[]> | null = null;
const analyticsDetailCache = new Map<string, Analytics>();
const analyticsDetailRequestPromises = new Map<string, Promise<Analytics | null>>();
const sectorStockSignalsCache = new Map<string, { [sector: string]: SectorStockSignal[] }>();
const sectorStockSignalsRequestPromises = new Map<string, Promise<{ [sector: string]: SectorStockSignal[] } | null>>();
let isSectorArchiveMerged = false;

const accumulatedStocksCache = new Map<string, string[]>();
const accumulatedStocksRequestPromises = new Map<string, Promise<string[]>>();
const accumulationCountCache = new Map<string, Record<string, string[]>>();
const accumulationPayloadCache = new Map<string, AccumulationCountPayload>();
const accumulationPayloadRequestPromises = new Map<string, Promise<AccumulationCountPayload>>();
const volumeShockersCache = new Map<string, VolumeShocker[]>();
const volumeShockersRequestPromises = new Map<string, Promise<VolumeShocker[]>>();
const overSoldCache = new Map<string, string[]>();
const overSoldRequestPromises = new Map<string, Promise<string[]>>();
const overboughtCache = new Map<string, string[]>();
const overboughtRequestPromises = new Map<string, Promise<string[]>>();
const ema21_50_200_StocksCache = new Map<string, string[]>();
const ema21_50_200_StocksRequestPromises = new Map<string, Promise<string[]>>();
const breakoutsCache = new Map<string, BreakoutStockItem[]>();
const breakoutsRequestPromises = new Map<string, Promise<BreakoutStockItem[]>>();
const approachingBreakoutsCache = new Map<string, BreakoutStockItem[]>();
const approachingBreakoutsRequestPromises = new Map<string, Promise<BreakoutStockItem[]>>();
const equityDeliveryCache = new Map<string, EquityDeliveryRecord[]>();
const equityDeliveryRequestPromises = new Map<string, Promise<EquityDeliveryRecord[]>>();

let sectorUniverseCache: SectorUniverse | null = sectorUniverseData as SectorUniverse;
let sectorUniverseRequestPromise: Promise<SectorUniverse> | null = null;
const indexHistoryCache = new Map<string, IndexHistory>();
const indexHistoryRequestPromises = new Map<string, Promise<IndexHistory>>();
let indexLatestCache: IndexLatestQuote[] | null = null;
let indexLatestRequestPromise: Promise<IndexLatestQuote[]> | null = null;
const indexDailyCache = new Map<string, IndexLatestQuote[]>();
const indexDailyRequestPromises = new Map<string, Promise<IndexLatestQuote[]>>();
let volumeStreaksCache: VolumeAboveAverageStreak[] | null = null;
let volumeStreaksRequestPromise: Promise<VolumeAboveAverageStreak[]> | null = null;
let priceMoversCache: ThirtyDayPriceMover[] | null = null;
let priceMoversRequestPromise: Promise<ThirtyDayPriceMover[]> | null = null;

const inflightRawRequests = new Map<string, Promise<any>>();
let sectorArchiveRequestPromise: Promise<SectorSummary[]> | null = null;
let allStocksCache: string[] | null = null;
let allStocksRequestPromise: Promise<string[]> | null = null;
const stockHistoryApiCache = new Map<string, StockHistory>();
const stockHistoryRequestPromises = new Map<string, Promise<StockHistory>>();
const marketDrawdownCache = new Map<string, any>();
const marketDrawdownRequestPromises = new Map<string, Promise<any | null>>();
let indexAvailableDatesCache: string[] | null = null;
let indexAvailableDatesPromise: Promise<string[]> | null = null;
let corporateFeedsLatestCache: { data: CorporateFeedItem[]; timestamp: number } | null = null;
let corporateFeedsLatestPromise: Promise<CorporateFeedItem[]> | null = null;
let corporateFeedsMetadataCache: { data: FeedRunMetadata; timestamp: number } | null = null;
let corporateFeedsMetadataPromise: Promise<FeedRunMetadata | null> | null = null;
const corporateFeedsDailyCache = new Map<string, CorporateFeedItem[]>();
const corporateFeedsDailyPromises = new Map<string, Promise<CorporateFeedItem[]>>();
const corporateFeedsCompanyCache = new Map<string, CorporateFeedItem[]>();
const corporateFeedsCompanyPromises = new Map<string, Promise<CorporateFeedItem[]>>();
let corporateCatalystsSummaryCache: CatalystsSummaryIndex | null = null;
let corporateCatalystsSummaryPromise: Promise<CatalystsSummaryIndex | null> | null = null;
let calculatorStatusPromise: Promise<CalculatorStatus | null> | null = null;
const CORPORATE_FEEDS_MEMORY_TTL_MS = 15 * 60 * 1000;

const getHistoryDataUrl = (symbol: string): string =>
  `${API_CONFIG.BASE_URL}${API_CONFIG.ENDPOINTS.STOCK_HISTORY(symbol)}`;

const getIndexHistoryUrl = (fileSafeIndexName: string): string =>
  `${API_CONFIG.BASE_URL}${API_CONFIG.ENDPOINTS.INDEX_HISTORY(fileSafeIndexName)}`;

const getIndexDailyUrl = (date: string): string =>
  `${API_CONFIG.BASE_URL}${API_CONFIG.ENDPOINTS.INDEX_DAILY(date)}`;

const getAnalyticsDetailUrl = (date: string): string =>
  `${API_CONFIG.BASE_URL}${API_CONFIG.ENDPOINTS.ANALYTICS_DETAIL(date)}`;

const getAccumulatedStocksUrl = (date: string): string =>
  `${API_CONFIG.BASE_URL}${API_CONFIG.ENDPOINTS.ACCUMULATED_STOCKS(date)}`;

const getAccumulationCountUrl = (date: string): string =>
  `${API_CONFIG.BASE_URL}${API_CONFIG.ENDPOINTS.ACCUMULATION_COUNT(date)}`;

const getVolumeShockersUrl = (date: string): string =>
  `${API_CONFIG.BASE_URL}${API_CONFIG.ENDPOINTS.VOLUME_SHOCKERS(date)}`;

const getOverSoldUrl = (date: string): string =>
  `${API_CONFIG.BASE_URL}${API_CONFIG.ENDPOINTS.OVERSOLD(date)}`;

const getOverboughtUrl = (date: string): string =>
  `${API_CONFIG.BASE_URL}${API_CONFIG.ENDPOINTS.OVERBOUGHT(date)}`;

const getEma21_50_200_StocksUrl = (date: string): string =>
  `${API_CONFIG.BASE_URL}${API_CONFIG.ENDPOINTS.EMA21_50_200_STOCKS(date)}`;

const getBreakoutsUrl = (date: string): string =>
  `${API_CONFIG.BASE_URL}${API_CONFIG.ENDPOINTS.BREAKOUTS(date)}`;

const getApproachingBreakoutsUrl = (date: string): string =>
  `${API_CONFIG.BASE_URL}${API_CONFIG.ENDPOINTS.APPROACHING_BREAKOUTS(date)}`;

const getEquityDeliveryUrl = (date: string): string =>
  `${API_CONFIG.BASE_URL}${API_CONFIG.ENDPOINTS.EQUITY_DELIVERY(date)}`;

const getVolumeStreaksUrl = (): string =>
  `${API_CONFIG.BASE_URL}${API_CONFIG.ENDPOINTS.VOLUME_STREAKS}`;

const getPriceMoversUrl = (): string =>
  `${API_CONFIG.BASE_URL}${API_CONFIG.ENDPOINTS.PRICE_MOVERS}`;

const marketDrawdownLatestUrl = `${API_CONFIG.BASE_URL}${API_CONFIG.ENDPOINTS.MARKET_DRAWDOWN_LATEST}`;
const getMarketDrawdownDailyUrl = (date: string): string =>
  `${API_CONFIG.BASE_URL}${API_CONFIG.ENDPOINTS.MARKET_DRAWDOWN_DAILY(date)}`;

const corporateFeedsLatestUrl = `${API_CONFIG.BASE_URL}${API_CONFIG.ENDPOINTS.CORPORATE_FEEDS_LATEST}`;
const corporateFeedsMetadataUrl = `${API_CONFIG.BASE_URL}${API_CONFIG.ENDPOINTS.CORPORATE_FEEDS_METADATA}`;
const getCorporateFeedsDailyUrl = (date: string): string =>
  `${API_CONFIG.BASE_URL}${API_CONFIG.ENDPOINTS.CORPORATE_FEEDS_DAILY(date)}`;
const getCorporateFeedsCompanyUrl = (symbol: string): string =>
  `${API_CONFIG.BASE_URL}${API_CONFIG.ENDPOINTS.CORPORATE_FEEDS_COMPANY(encodeURIComponent(symbol))}`;
const corporateFeedsCatalystsSummaryUrl = `${API_CONFIG.BASE_URL}${API_CONFIG.ENDPOINTS.CORPORATE_FEEDS_CATALYSTS_SUMMARY}`;

/**
 * Checks if a target URL or date parameter references a strictly past historical date.
 * Once an Indian trading day is in the past, its EOD Bhavcopy and calculations are immutable.
 */
export const isHistoricalDateUrl = (url?: string | null): boolean => {
  if (!url) return false;
  const match = url.match(/\b(20\d{2})-(0[1-9]|1[0-2])-(0[1-9]|[12]\d|3[01])\b/);
  if (!match) return false;
  const todayIst = new Intl.DateTimeFormat('en-CA', { timeZone: 'Asia/Kolkata' }).format(new Date());
  return match[0] < todayIst;
};

/**
 * Builds the canonical API URL.
 * - Historical past dates (< today) are immutable: never appended with query cache-busters.
 * - Live mutable requests leverage HTTP ETags (304 Not Modified) rather than time-slot query busting,
 *   saving 80%+ egress bandwidth while guaranteeing zero stale data.
 * - Explicit fresh requests append `?fresh=true` to force upstream revalidation.
 */
export const buildApiUrl = (url: string, isFresh = false): string => {
  if (isHistoricalDateUrl(url)) {
    return url;
  }
  if (isFresh) {
    const separator = url.includes('?') ? '&' : '?';
    return `${url}${separator}fresh=true&t=${Date.now()}`;
  }
  return url;
};

const buildFreshUrl = (url: string): string => buildApiUrl(url, true);
const buildDailyFreshUrl = (url: string): string => buildApiUrl(url, false);

const hasLocalStorage = (): boolean => typeof window !== 'undefined' && typeof window.localStorage !== 'undefined';

const sortAnalyticsData = (data: Analytics[]): Analytics[] =>
  [...data].sort((left, right) => new Date(right.Date).getTime() - new Date(left.Date).getTime());

const normalizeBreakoutStockItem = (
  item: any,
  defaultDate: string,
  defaultIsApproaching = false
): BreakoutStockItem => {
  if (typeof item === 'string') {
    return {
      StockName: item,
      CurrentPrice: 0,
      PreviousHigh: 0,
      CurrentVolume: 0,
      AvgVolume30Days: 0,
      VolumeRatio: 0,
      IsBrokenAboveHigh: !defaultIsApproaching,
      IsHighVolume: false,
      HasEMAAlignment: false,
      IsValidBreakout: true,
      Date: defaultDate,
      PercentageBreakout: 0,
      BreakoutType: 'High' as const,
      IsApproaching: defaultIsApproaching,
    };
  }
  const isApproaching =
    item.IsApproaching !== undefined ? Boolean(item.IsApproaching) : defaultIsApproaching;
  const distance =
    item.DistanceToBreakout != null ? Number(item.DistanceToBreakout) : undefined;

  return {
    StockName: item.StockName ?? '',
    CurrentPrice: Number(item.CurrentPrice ?? 0),
    PreviousHigh: Number(item.PreviousHigh ?? 0),
    CurrentVolume: Number(item.CurrentVolume ?? 0),
    AvgVolume30Days: Number(item.AvgVolume30Days ?? 0),
    VolumeRatio: Number(item.VolumeRatio ?? 0),
    IsBrokenAboveHigh: item.IsBrokenAboveHigh !== undefined ? Boolean(item.IsBrokenAboveHigh) : !isApproaching,
    IsHighVolume: Boolean(item.IsHighVolume),
    HasEMAAlignment: Boolean(item.HasEMAAlignment),
    IsValidBreakout: item.IsValidBreakout !== undefined ? Boolean(item.IsValidBreakout) : true,
    Date: item.Date ?? defaultDate,
    PercentageBreakout: Number(item.PercentageBreakout ?? 0),
    BreakoutType: item.BreakoutType ?? 'High',
    PivotPrice: item.PivotPrice != null ? Number(item.PivotPrice) : undefined,
    PivotDate: item.PivotDate ?? undefined,
    LookbackDays: item.LookbackDays != null ? Number(item.LookbackDays) : undefined,
    DistanceToBreakout: distance,
    IsApproaching: isApproaching,
    ResistanceClosePrice: item.ResistanceClosePrice != null ? Number(item.ResistanceClosePrice) : undefined,
    TouchCount: item.TouchCount != null ? Number(item.TouchCount) : undefined,
    AnchoredVwapPrice: item.AnchoredVwapPrice != null ? Number(item.AnchoredVwapPrice) : undefined,
  };
};

const normalizeAnalytics = (value: Partial<Analytics>): Analytics => {
  const breakoutStocks: BreakoutStockItem[] = Array.isArray(value.BreakoutStocks)
    ? value.BreakoutStocks
      .filter(Boolean)
      .map((item: any) => normalizeBreakoutStockItem(item, value.Date ?? '', false))
      .filter((item) => Boolean(item.StockName))
    : [];

  const approachingBreakoutStocks: BreakoutStockItem[] = Array.isArray(value.ApproachingBreakoutStocks)
    ? value.ApproachingBreakoutStocks
      .filter(Boolean)
      .map((item: any) => normalizeBreakoutStockItem(item, value.Date ?? '', true))
      .filter((item) => Boolean(item.StockName))
    : [];
  const volumeShockers = Array.isArray(value.VolumeShockers) ? value.VolumeShockers : [];
  const volumeAboveAverageStreaks = Array.isArray(value.VolumeAboveAverageStreaks)
    ? value.VolumeAboveAverageStreaks
      .filter((item): item is NonNullable<Analytics['VolumeAboveAverageStreaks']>[number] => Boolean(item?.Symbol))
      .map((item) => ({
        Symbol: item.Symbol,
        ConsecutiveDays: Number(item.ConsecutiveDays ?? 0),
        LatestVolume: Number(item.LatestVolume ?? 0),
        LatestAverageVolume: item.LatestAverageVolume == null ? null : Number(item.LatestAverageVolume),
        Multiplier: item.Multiplier == null ? null : Number(item.Multiplier),
        DeliveryPercentage: Number(item.DeliveryPercentage ?? 0),
      }))
    : [];
  const overSold = Array.isArray(value.OverSold) ? value.OverSold.filter(Boolean) : [];
  const overbought = Array.isArray(value.Overbought) ? value.Overbought.filter(Boolean) : [];
  const thirtyDayPriceMovers = Array.isArray(value.ThirtyDayPriceMovers)
    ? value.ThirtyDayPriceMovers
      .filter((item): item is NonNullable<Analytics['ThirtyDayPriceMovers']>[number] => Boolean(item?.Symbol))
      .map((item) => ({
        Symbol: item.Symbol,
        LowestCloseDate: item.LowestCloseDate ?? '',
        LowestClosePrice: Number(item.LowestClosePrice ?? 0),
        CurrentClosePrice: Number(item.CurrentClosePrice ?? 0),
        PercentageIncrease: Number(item.PercentageIncrease ?? 0),
      }))
    : [];
  const normalizeNestedSectorNumberMap = (map: Partial<Record<string, Partial<Record<string, number>>>> | undefined) =>
    Object.fromEntries(
      Object.entries(map ?? {}).map(([sector, subSectors]) => [
        sector,
        Object.fromEntries(
          Object.entries(subSectors ?? {}).map(([subSector, count]) => [subSector, count ?? 0]),
        ),
      ]),
    );
  const normalizeSectorStockSignals = (map: Partial<Record<string, Array<any>>> | undefined) =>
    Object.fromEntries(
      Object.entries(map ?? {}).map(([sector, rows]) => [
        sector,
        (Array.isArray(rows) ? rows : [])
          .filter((row) => Boolean(row?.Symbol))
          .map((row) => ({
            Symbol: String(row.Symbol).toUpperCase(),
            SubSector: row.SubSector ?? '',
            PerChange: Number(row.PerChange ?? 0),
            IsGreenClose: Boolean(row.IsGreenClose),
            IsAccumulated: Boolean(row.IsAccumulated),
            IsAbove21Ema: Boolean(row.IsAbove21Ema),
            IsBreakout: Boolean(row.IsBreakout),
          })),
      ]),
    );

  return {
    Date: value.Date ?? '',
    accumulationCount: value.accumulationCount ?? {},
    accumulationRatio: value.accumulationRatio ?? 0,
    distributionRatio: value.distributionRatio ?? 0,
    Above21Ema: value.Above21Ema ?? 0,
    Below21Ema: value.Below21Ema ?? 0,
    Above50Ema: value.Above50Ema ?? 0,
    Below50Ema: value.Below50Ema ?? 0,
    Above200Ema: value.Above200Ema ?? 0,
    Below200Ema: value.Below200Ema ?? 0,
    AbvAllEma: value.AbvAllEma ?? 0,
    BelowAllEma: value.BelowAllEma ?? 0,
    NegativeClose: value.NegativeClose ?? 0,
    PositiveClose: value.PositiveClose ?? 0,
    FourPerPositiveClose: value.FourPerPositiveClose ?? 0,
    FourPerNegativeClose: value.FourPerNegativeClose ?? 0,
    Closing52WeekHighCount: value.Closing52WeekHighCount ?? 0,
    Closing52WeekLowCount: value.Closing52WeekLowCount ?? 0,
    TtlAccCount: value.TtlAccCount ?? 0,
    TtlVolumeCount: value.TtlVolumeCount ?? 0,
    TtlDistributedCount: value.TtlDistributedCount ?? 0,
    TtlNity500VolumneCount: value.TtlNity500VolumneCount ?? 0,
    TtlNifty500AccCount: value.TtlNifty500AccCount ?? 0,
    BreakoutStockCount: value.BreakoutStockCount ?? breakoutStocks.length,
    ApproachingBreakoutCount: value.ApproachingBreakoutCount ?? approachingBreakoutStocks.length,
    VolumeShockerCount: value.VolumeShockerCount ?? volumeShockers.length,
    VolumeAboveAverageStreaks: volumeAboveAverageStreaks,
    OverSoldCount: value.OverSoldCount ?? overSold.length,
    OverboughtCount: value.OverboughtCount ?? overbought.length,
    accumulatedStocks: Array.isArray(value.accumulatedStocks) ? value.accumulatedStocks.filter(Boolean) : [],
    VolumeShockers: volumeShockers,
    lstAbv21Ema: Array.isArray(value.lstAbv21Ema) ? value.lstAbv21Ema.filter(Boolean) : [],
    lstAbvAllEma: Array.isArray(value.lstAbvAllEma) ? value.lstAbvAllEma.filter(Boolean) : [],
    Ema21_50_200_Stocks: Array.isArray(value.Ema21_50_200_Stocks) ? value.Ema21_50_200_Stocks.filter(Boolean) : [],
    Ema21_50_200_Count: typeof value.Ema21_50_200_Count === 'number' && !isNaN(value.Ema21_50_200_Count)
      ? value.Ema21_50_200_Count
      : (Array.isArray(value.Ema21_50_200_Stocks) && value.Ema21_50_200_Stocks.length > 0
        ? value.Ema21_50_200_Stocks.length
        : undefined),
    BreakoutStocks: breakoutStocks,
    ApproachingBreakoutStocks: approachingBreakoutStocks,
    OverSold: overSold,
    Overbought: overbought,
    SectorAbove21Ema: mergeSectorNumberMap((value.SectorAbove21Ema && Object.keys(value.SectorAbove21Ema).length > 0) ? value.SectorAbove21Ema : (value.IndustryAbove21Ema ?? {})),
    SectorAccumulatedCount: mergeSectorNumberMap((value.SectorAccumulatedCount && Object.keys(value.SectorAccumulatedCount).length > 0) ? value.SectorAccumulatedCount : (value.IndustryAccumulatedCount ?? {})),
    SectorTotalStocks: mergeSectorNumberMap((value.SectorTotalStocks && Object.keys(value.SectorTotalStocks).length > 0) ? value.SectorTotalStocks : (value.IndustryTotalStocks ?? {})),
    SectorDeliveredValueCrores: mergeSectorNumberMap(value.SectorDeliveredValueCrores ?? {}),
    SectorClosing52WeekHighCount: mergeSectorNumberMap(value.SectorClosing52WeekHighCount ?? {}),
    SectorClosing52WeekLowCount: mergeSectorNumberMap(value.SectorClosing52WeekLowCount ?? {}),
    SectorPositiveCloseCount: mergeSectorNumberMap(value.SectorPositiveCloseCount ?? {}),
    SectorStocksAbove21Ema: mergeSectorStringArrayMap(value.SectorStocksAbove21Ema ?? value.IndustryStocksAbove21Ema ?? {}),
    SectorAccumulatedStocks: mergeSectorStringArrayMap(value.SectorAccumulatedStocks ?? value.IndustryAccumulatedStocks ?? {}),
    SectorStockSignals: mergeSectorSignalsMap(normalizeSectorStockSignals(value.SectorStockSignals as Partial<Record<string, Array<any>>> | undefined)),
    SubSectorAbove21Ema: mergeNestedSectorMap(normalizeNestedSectorNumberMap(value.SubSectorAbove21Ema)),
    SubSectorAccumulatedCount: mergeNestedSectorMap(normalizeNestedSectorNumberMap(value.SubSectorAccumulatedCount)),
    SubSectorStocksAbove21Ema: mergeNestedSectorMap((value as any).SubSectorStocksAbove21Ema ?? {}),
    SubSectorAccumulatedStocks: mergeNestedSectorMap((value as any).SubSectorAccumulatedStocks ?? {}),
    SubSectorTotalStocks: mergeNestedSectorMap(normalizeNestedSectorNumberMap(value.SubSectorTotalStocks)),
    SubSectorDeliveredValueCrores: mergeNestedSectorMap(normalizeNestedSectorNumberMap(value.SubSectorDeliveredValueCrores)),
    SubSectorClosing52WeekHighCount: mergeNestedSectorMap(normalizeNestedSectorNumberMap(value.SubSectorClosing52WeekHighCount)),
    SubSectorClosing52WeekLowCount: mergeNestedSectorMap(normalizeNestedSectorNumberMap(value.SubSectorClosing52WeekLowCount)),
    SubSectorPositiveCloseCount: mergeNestedSectorMap(normalizeNestedSectorNumberMap(value.SubSectorPositiveCloseCount)),
    DarvasBoxCandidates: Array.isArray(value.DarvasBoxCandidates) ? value.DarvasBoxCandidates.filter(Boolean) : [],
    ThirtyDayPriceMovers: thirtyDayPriceMovers,
    Sma21_50_200_Stocks: Array.isArray(value.Sma21_50_200_Stocks) ? value.Sma21_50_200_Stocks.filter(Boolean) : [],
  };
};

const normalizeAnalyticsCollection = (data: Partial<Analytics>[]): Analytics[] =>
  sortAnalyticsData((Array.isArray(data) ? data : []).map((item) => normalizeAnalytics(item)));


const getAuthHeaders = async (): Promise<Record<string, string>> => {
  try {
    let token = await getAuthToken();
    if (!token) {
      // Brief delay in case Firebase auth state is still restoring from storage
      await new Promise((resolve) => setTimeout(resolve, 250));
      token = await getAuthToken();
    }
    return token
      ? {
        Authorization: `Bearer ${token}`,
        'X-Authorization': `Bearer ${token}`,
      }
      : {};
  } catch {
    return {};
  }
};

const getJsonFallbackUrl = (url: string): string | null => {
  if (url.includes('.dat')) {
    return url.replace(/\.dat(\?|$)/, '.json$1');
  }
  return null;
};

const parseResponsePayload = async <T>(response: Response, url: string): Promise<T> => {
  const cleanUrl = url.split('?')[0].toLowerCase();
  const contentType = (response.headers.get('content-type') || '').toLowerCase();
  const isBinary = cleanUrl.endsWith('.dat') || contentType.includes('application/octet-stream');

  if (isBinary) {
    const buffer = await response.arrayBuffer();
    try {
      return await decryptJsonResponse<T>(buffer);
    } catch (decryptErr) {
      // If decryption failed, check if payload is plain JSON (e.g. unencrypted mock or proxy)
      try {
        const text = new TextDecoder('utf-8').decode(buffer);
        const trimmed = text.trim();
        if (trimmed.startsWith('{') || trimmed.startsWith('[')) {
          return JSON.parse(trimmed) as T;
        }
      } catch {
        // Not plain JSON
      }
      throw decryptErr;
    }
  }

  return (await response.json()) as T;
};

const fetchRawPayload = async <T>(targetUrl: string, options?: { dailyCache?: boolean; fresh?: boolean }): Promise<T> => {
  const isFresh = options?.fresh === true;
  if (!isFresh && inflightRawRequests.has(targetUrl)) {
    return inflightRawRequests.get(targetUrl) as Promise<T>;
  }

  const requestPromise = (async () => {
    const authHeaders = await getAuthHeaders();
    const finalUrl = buildApiUrl(targetUrl, isFresh);
    const fetchOptions: RequestInit = {
      headers: {
        ...authHeaders,
        ...(isFresh
          ? {
            'Cache-Control': 'no-cache, no-store, must-revalidate',
            Pragma: 'no-cache',
            Expires: '0',
          }
          : {}),
      },
      ...(isFresh ? { cache: 'no-store' } : {}),
    };

    const response = await fetch(finalUrl, fetchOptions);

    if (!response.ok) {
      throw new ApiError(`HTTP ${response.status}: ${response.statusText}`, response.status, targetUrl);
    }

    return await parseResponsePayload<T>(response, targetUrl);
  })();

  if (!isFresh) {
    inflightRawRequests.set(targetUrl, requestPromise);
  }

  try {
    return await requestPromise;
  } finally {
    if (!isFresh) {
      inflightRawRequests.delete(targetUrl);
    }
  }
};

const readJsonResponse = async <T>(url: string, options?: { dailyCache?: boolean; fresh?: boolean }): Promise<T> => {
  const isDatUrl = url.includes('.dat');

  if (isDatUrl) {
    const jsonFallbackUrl = getJsonFallbackUrl(url);
    try {
      return await fetchRawPayload<T>(url, options);
    } catch (datError) {
      console.warn(`[NiftySutra API] .dat file failed to load from ${url}. Falling back to .json:`, datError);
      if (jsonFallbackUrl) {
        return await fetchRawPayload<T>(jsonFallbackUrl, options);
      }
      throw datError;
    }
  }

  return await fetchRawPayload<T>(url, options);
};

const readPersistentAnalyticsCache = (): Analytics[] | null => {
  if (!hasLocalStorage()) {
    return null;
  }

  try {
    const cachedValue = window.localStorage.getItem(ANALYTICS_STORAGE_KEY);
    if (!cachedValue) {
      return null;
    }

    const parsedCache = JSON.parse(cachedValue) as { data?: Analytics[] };
    if (!Array.isArray(parsedCache.data)) {
      window.localStorage.removeItem(ANALYTICS_STORAGE_KEY);
      return null;
    }

    if (parsedCache.data.length > 300) {
      isArchiveMerged = true;
    }

    return parsedCache.data;
  } catch (error) {
    console.warn('Failed to read analytics cache from localStorage:', error);
    return null;
  }
};

const writePersistentAnalyticsCache = (data: Analytics[]): void => {
  if (!hasLocalStorage()) {
    return;
  }

  try {
    const serializedCache = JSON.stringify({
      data,
    });

    if (serializedCache.length > MAX_PERSISTED_ANALYTICS_BYTES) {
      window.localStorage.removeItem(ANALYTICS_STORAGE_KEY);
      return;
    }

    window.localStorage.setItem(ANALYTICS_STORAGE_KEY, serializedCache);
  } catch (error) {
    console.warn('Failed to persist analytics cache to localStorage:', error);
  }
};

const fetchLegacyAnalytics = async (): Promise<Analytics[]> => {
  // Legacy analytics.json has been removed – we always fetch fresh data from the API.
  // This fallback now returns an empty array.
  return [];
};

const fetchAnalyticsSummaryPayload = async (options?: { fresh?: boolean }): Promise<Analytics[]> => {
  try {
    // Use canonical URL with HTTP ETag validation (304 Not Modified) unless user explicitly requests force refresh
    const isFresh = Boolean(options?.fresh);
    const parsed = normalizeAnalyticsCollection(
      await readJsonResponse<Analytics[]>(analyticsSummaryUrl, { fresh: isFresh, dailyCache: !isFresh })
    );

    console.log('=== Analytics Summary Response ===');
    console.log('Total records:', parsed.length);
    console.log('Latest record date:', parsed[0]?.Date);

    return parsed;
  } catch (error) {
    console.warn('Failed to load analytics summary, returning cached analytics:', error);
    return fetchLegacyAnalytics();
  }
};

/**
 * Resets all in-memory dataset caches and clears persisted localStorage analytics cache
 * so background updates or calculator runs can immediately load fresh data.
 */
export const clearApiCaches = (): void => {
  analyticsCache = null;
  analyticsRequestPromise = null;
  isArchiveMerged = false;
  archiveRequestPromise = null;
  analyticsDetailCache.clear();
  analyticsDetailRequestPromises.clear();
  accumulatedStocksCache.clear();
  accumulatedStocksRequestPromises.clear();
  accumulationCountCache.clear();
  accumulationPayloadCache.clear();
  accumulationPayloadRequestPromises.clear();
  volumeShockersCache.clear();
  volumeShockersRequestPromises.clear();
  overSoldCache.clear();
  overSoldRequestPromises.clear();
  overboughtCache.clear();
  overboughtRequestPromises.clear();
  ema21_50_200_StocksCache.clear();
  ema21_50_200_StocksRequestPromises.clear();
  breakoutsCache.clear();
  breakoutsRequestPromises.clear();
  approachingBreakoutsCache.clear();
  approachingBreakoutsRequestPromises.clear();
  equityDeliveryCache.clear();
  equityDeliveryRequestPromises.clear();
  indexDailyCache.clear();
  indexDailyRequestPromises.clear();
  indexLatestCache = null;
  indexLatestRequestPromise = null;
  sectorSummaryCache = null;
  sectorSummaryRequestPromise = null;
  sectorArchiveRequestPromise = null;
  isSectorArchiveMerged = false;
  sectorStockSignalsCache.clear();
  sectorStockSignalsRequestPromises.clear();
  indexHistoryCache.clear();
  indexHistoryRequestPromises.clear();
  volumeStreaksCache = null;
  volumeStreaksRequestPromise = null;
  priceMoversCache = null;
  priceMoversRequestPromise = null;
  inflightRawRequests.clear();
  allStocksCache = null;
  allStocksRequestPromise = null;
  stockHistoryApiCache.clear();
  stockHistoryRequestPromises.clear();
  marketDrawdownCache.clear();
  marketDrawdownRequestPromises.clear();
  indexAvailableDatesCache = null;
  indexAvailableDatesPromise = null;
  corporateFeedsLatestCache = null;
  corporateFeedsLatestPromise = null;
  corporateFeedsMetadataCache = null;
  corporateFeedsMetadataPromise = null;
  corporateFeedsDailyCache.clear();
  corporateFeedsDailyPromises.clear();
  corporateFeedsCompanyCache.clear();
  corporateFeedsCompanyPromises.clear();
  corporateCatalystsSummaryCache = null;
  corporateCatalystsSummaryPromise = null;
  calculatorStatusPromise = null;
  try {
    if (typeof window !== 'undefined' && window.localStorage) {
      window.localStorage.removeItem(ANALYTICS_STORAGE_KEY);
    }
  } catch {
    // Ignore storage errors in restricted contexts
  }
};


/**
 * Handle API errors and convert to ApiError
 */
const handleApiError = (error: unknown, endpoint: string): never => {
  if (axios.isAxiosError(error)) {
    const axiosError = error as AxiosError;
    throw new ApiError(
      `Failed to fetch from ${endpoint}: ${axiosError.message}`,
      axiosError.response?.status,
      endpoint
    );
  }
  if (error instanceof Error) {
    const match = error.message.match(/HTTP (\d+):/);
    const statusCode = match ? parseInt(match[1], 10) : undefined;
    throw new ApiError(error.message, statusCode, endpoint);
  }
  throw new ApiError(`Unexpected error fetching from ${endpoint}`, undefined, endpoint);
};

/**
 * Fetch analytics data with retry logic using native fetch to preserve all JSON properties
 * @returns Promise resolving to analytics array
 */
export const getCachedAnalyticsData = (): Analytics[] | null =>
  (() => {
    if (analyticsCache) {
      return [...analyticsCache];
    }

    const persistentCache = readPersistentAnalyticsCache();
    if (persistentCache) {
      // NOTE: Do NOT assign `analyticsCache = persistentCache` here.
      // If assigned, subsequent initial `fetchData({ forceRefresh: false })` calls
      // would treat the stale persistent localStorage data as an active session cache
      // and skip network synchronization, permanently stranding the user on older data.
      return [...persistentCache];
    }

    return null;
  })();

export const fetchAccumulatedStocksDetail = async (
  date: string,
  options?: { forceRefresh?: boolean }
): Promise<string[]> => {
  const forceRefresh = options?.forceRefresh ?? false;
  if (!date) return [];
  const token = await getAuthToken();
  if (!token) return [];
  if (!forceRefresh && accumulatedStocksCache.has(date)) {
    return accumulatedStocksCache.get(date) ?? [];
  }
  if (!forceRefresh && accumulatedStocksRequestPromises.has(date)) {
    return accumulatedStocksRequestPromises.get(date) ?? Promise.resolve([]);
  }

  const promise = retryWithBackoff(async () => {
    try {
      const data = await readJsonResponse<string[]>(getAccumulatedStocksUrl(date));
      const normalized = Array.isArray(data) ? data.filter(Boolean) : [];
      if (normalized.length > 0) {
        accumulatedStocksCache.set(date, normalized);
        return normalized;
      }
    } catch {
      // Split datewise file 404s (e.g. pre-Sept 11) -> fall back to consolidated analytics detail
    }

    try {
      const detail = await fetchAnalyticsDetail(date, options);
      const fallback = detail?.accumulatedStocks ?? [];
      accumulatedStocksCache.set(date, fallback);
      return fallback;
    } catch {
      return [];
    }
  });

  accumulatedStocksRequestPromises.set(date, promise);
  try {
    return await promise;
  } finally {
    accumulatedStocksRequestPromises.delete(date);
  }
};

export const fetchAccumulationCountDetail = async (
  date: string,
  options?: { forceRefresh?: boolean }
): Promise<AccumulationCountPayload> => {
  const forceRefresh = options?.forceRefresh ?? false;
  if (!date) return { streaks: {}, metrics: {} };
  if (!forceRefresh && accumulationPayloadCache.has(date)) {
    return accumulationPayloadCache.get(date) ?? { streaks: {}, metrics: {} };
  }
  if (!forceRefresh && accumulationPayloadRequestPromises.has(date)) {
    return accumulationPayloadRequestPromises.get(date) ?? Promise.resolve({ streaks: {}, metrics: {} });
  }

  const promise = retryWithBackoff(async () => {
    try {
      const data = await readJsonResponse<any>(getAccumulationCountUrl(date));
      let streaks: Record<string, string[]> = {};
      let metrics: Record<string, StockSessionMetric> = {};

      if (data && typeof data === 'object' && ('streaks' in data || 'metrics' in data)) {
        streaks = typeof data.streaks === 'object' && data.streaks !== null
          ? Object.fromEntries(
            Object.entries(data.streaks).map(([key, list]) => [
              key,
              Array.isArray(list) ? list.filter(Boolean) : [],
            ])
          )
          : {};
        metrics = typeof data.metrics === 'object' && data.metrics !== null ? data.metrics : {};
      } else if (typeof data === 'object' && data !== null) {
        streaks = Object.fromEntries(
          Object.entries(data).map(([key, list]) => [
            key,
            Array.isArray(list) ? list.filter(Boolean) : [],
          ])
        );
      }

      const result: AccumulationCountPayload = { streaks, metrics };
      if (Object.keys(streaks).length > 0 || Object.keys(metrics).length > 0) {
        accumulationPayloadCache.set(date, result);
        accumulationCountCache.set(date, streaks);
        return result;
      }
    } catch {
      // Split datewise file 404s (e.g. pre-Sept 11) -> fall back to consolidated analytics detail
    }

    try {
      const detail = await fetchAnalyticsDetail(date, options);
      const fallbackStreaks = detail?.accumulationCount ?? {};
      const fallbackResult: AccumulationCountPayload = { streaks: fallbackStreaks, metrics: {} };
      accumulationPayloadCache.set(date, fallbackResult);
      accumulationCountCache.set(date, fallbackStreaks);
      return fallbackResult;
    } catch {
      return { streaks: {}, metrics: {} };
    }
  });

  accumulationPayloadRequestPromises.set(date, promise);
  try {
    return await promise;
  } finally {
    accumulationPayloadRequestPromises.delete(date);
  }
};

export const fetchVolumeShockersDetail = async (
  date: string,
  options?: { forceRefresh?: boolean }
): Promise<VolumeShocker[]> => {
  const forceRefresh = options?.forceRefresh ?? false;
  if (!date) return [];
  if (!forceRefresh && volumeShockersCache.has(date)) {
    return volumeShockersCache.get(date) ?? [];
  }
  if (!forceRefresh && volumeShockersRequestPromises.has(date)) {
    return volumeShockersRequestPromises.get(date) ?? Promise.resolve([]);
  }

  const promise = retryWithBackoff(async () => {
    try {
      const data = await readJsonResponse<VolumeShocker[]>(getVolumeShockersUrl(date));
      const normalized: VolumeShocker[] = Array.isArray(data)
        ? data
          .filter((item): item is VolumeShocker => Boolean(item?.Symbol))
          .map((item) => ({
            Symbol: String(item.Symbol).toUpperCase(),
            DelieveryPercentage: Number(item.DelieveryPercentage ?? 0),
            Multiplier: Number(item.Multiplier ?? 0),
            EquityDeliveryPercentage: item.EquityDeliveryPercentage != null ? Number(item.EquityDeliveryPercentage) : undefined,
            IsHighConviction5Percent: Boolean(item.IsHighConviction5Percent),
          }))
        : [];
      if (normalized.length > 0) {
        volumeShockersCache.set(date, normalized);
        return normalized;
      }
    } catch {
      // Split datewise file 404s (e.g. pre-Sept 11) -> fall back to consolidated analytics detail
    }

    try {
      const detail = await fetchAnalyticsDetail(date, options);
      const fallback = detail?.VolumeShockers ?? [];
      volumeShockersCache.set(date, fallback);
      return fallback;
    } catch {
      return [];
    }
  });

  volumeShockersRequestPromises.set(date, promise);
  try {
    return await promise;
  } finally {
    volumeShockersRequestPromises.delete(date);
  }
};

export const fetchEquityDeliveryDetail = async (
  date: string,
  options?: { forceRefresh?: boolean }
): Promise<EquityDeliveryRecord[]> => {
  const forceRefresh = options?.forceRefresh ?? false;
  if (!date) return [];
  if (!forceRefresh && equityDeliveryCache.has(date)) {
    return equityDeliveryCache.get(date) ?? [];
  }
  if (!forceRefresh && equityDeliveryRequestPromises.has(date)) {
    return equityDeliveryRequestPromises.get(date) ?? Promise.resolve([]);
  }

  const promise = retryWithBackoff(async () => {
    try {
      const data = await readJsonResponse<EquityDeliveryRecord[]>(getEquityDeliveryUrl(date));
      const normalized: EquityDeliveryRecord[] = Array.isArray(data)
        ? data
          .filter((item): item is EquityDeliveryRecord => Boolean(item?.Symbol))
          .filter((item) => Number(item.EquityDeliveryPercentage ?? 0) >= 5.0 || Boolean(item.IsHighConviction5Percent))
          .map((item) => ({
            Symbol: String(item.Symbol).toUpperCase(),
            DelivQty: Number(item.DelivQty ?? 0),
            TotalShares: Number(item.TotalShares ?? 0),
            EquityDeliveryPercentage: Number(item.EquityDeliveryPercentage ?? 0),
            VolumeDeliveryPercentage: Number(item.VolumeDeliveryPercentage ?? 0),
            FreeFloatDeliveryPercentage: item.FreeFloatDeliveryPercentage != null ? Number(item.FreeFloatDeliveryPercentage) : null,
            DeliveredValueCrores: item.DeliveredValueCrores != null ? Number(item.DeliveredValueCrores) : null,
            ClosePrice: item.ClosePrice != null ? Number(item.ClosePrice) : null,
            PerChange: item.PerChange != null ? Number(item.PerChange) : null,
            IsHighConviction5Percent: Boolean(item.IsHighConviction5Percent || (item.EquityDeliveryPercentage ?? 0) >= 5.0),
          }))
        : [];
      equityDeliveryCache.set(date, normalized);
      return normalized;
    } catch {
      return [];
    }
  });

  equityDeliveryRequestPromises.set(date, promise);
  try {
    return await promise;
  } finally {
    equityDeliveryRequestPromises.delete(date);
  }
};

export const fetchOverSoldDetail = async (
  date: string,
  options?: { forceRefresh?: boolean }
): Promise<string[]> => {
  const forceRefresh = options?.forceRefresh ?? false;
  if (!date) return [];
  if (!forceRefresh && overSoldCache.has(date)) {
    return overSoldCache.get(date) ?? [];
  }
  if (!forceRefresh && overSoldRequestPromises.has(date)) {
    return overSoldRequestPromises.get(date) ?? Promise.resolve([]);
  }

  const promise = retryWithBackoff(async () => {
    try {
      const data = await readJsonResponse<string[]>(getOverSoldUrl(date));
      const normalized = Array.isArray(data) ? data.filter(Boolean) : [];
      if (normalized.length > 0) {
        overSoldCache.set(date, normalized);
        return normalized;
      }
    } catch {
      // Split datewise file 404s (e.g. pre-Sept 11) -> fall back to consolidated analytics detail
    }

    try {
      const detail = await fetchAnalyticsDetail(date, options);
      const fallback = detail?.OverSold ?? [];
      overSoldCache.set(date, fallback);
      return fallback;
    } catch {
      return [];
    }
  });

  overSoldRequestPromises.set(date, promise);
  try {
    return await promise;
  } finally {
    overSoldRequestPromises.delete(date);
  }
};

export const fetchOverboughtDetail = async (
  date: string,
  options?: { forceRefresh?: boolean }
): Promise<string[]> => {
  const forceRefresh = options?.forceRefresh ?? false;
  if (!date) return [];
  if (!forceRefresh && overboughtCache.has(date)) {
    return overboughtCache.get(date) ?? [];
  }
  if (!forceRefresh && overboughtRequestPromises.has(date)) {
    return overboughtRequestPromises.get(date) ?? Promise.resolve([]);
  }

  const promise = retryWithBackoff(async () => {
    try {
      const data = await readJsonResponse<string[]>(getOverboughtUrl(date));
      const normalized = Array.isArray(data) ? data.filter(Boolean) : [];
      if (normalized.length > 0) {
        overboughtCache.set(date, normalized);
        return normalized;
      }
    } catch {
      // Split datewise file 404s (e.g. pre-Sept 11) -> fall back to consolidated analytics detail
    }

    try {
      const detail = await fetchAnalyticsDetail(date, options);
      const fallback = detail?.Overbought ?? [];
      overboughtCache.set(date, fallback);
      return fallback;
    } catch {
      return [];
    }
  });

  overboughtRequestPromises.set(date, promise);
  try {
    return await promise;
  } finally {
    overboughtRequestPromises.delete(date);
  }
};

export const fetchEma21_50_200_StocksDetail = async (
  date: string,
  options?: { forceRefresh?: boolean; fallbackToAnalytics?: boolean }
): Promise<string[]> => {
  const forceRefresh = options?.forceRefresh ?? false;
  const fallbackToAnalytics = options?.fallbackToAnalytics ?? false;
  if (!date) return [];
  if (!forceRefresh && ema21_50_200_StocksCache.has(date)) {
    return ema21_50_200_StocksCache.get(date) ?? [];
  }
  if (!forceRefresh && ema21_50_200_StocksRequestPromises.has(date)) {
    return ema21_50_200_StocksRequestPromises.get(date) ?? Promise.resolve([]);
  }

  const promise = retryWithBackoff(async () => {
    try {
      const data = await readJsonResponse<string[]>(getEma21_50_200_StocksUrl(date));
      const normalized = Array.isArray(data) ? data.filter(Boolean) : [];
      if (normalized.length > 0) {
        ema21_50_200_StocksCache.set(date, normalized);
        return normalized;
      }
    } catch {
      // Split datewise file 404s
    }

    if (fallbackToAnalytics) {
      try {
        const detail = await fetchAnalyticsDetail(date, options);
        const fallback = detail?.Ema21_50_200_Stocks?.length
          ? detail.Ema21_50_200_Stocks
          : [];
        ema21_50_200_StocksCache.set(date, fallback);
        return fallback;
      } catch {
        return [];
      }
    }

    return [];
  });

  ema21_50_200_StocksRequestPromises.set(date, promise);
  try {
    return await promise;
  } finally {
    ema21_50_200_StocksRequestPromises.delete(date);
  }
};

export const getCachedEma21_50_200_StocksCount = (date: string): number | undefined => {
  const cached = ema21_50_200_StocksCache.get(date);
  return cached ? cached.length : undefined;
};

export const fetchBreakoutsDetail = async (
  date: string,
  options?: { forceRefresh?: boolean }
): Promise<BreakoutStockItem[]> => {
  const forceRefresh = options?.forceRefresh ?? false;
  if (!date) return [];
  if (!forceRefresh && breakoutsCache.has(date)) {
    return breakoutsCache.get(date) ?? [];
  }
  if (!forceRefresh && breakoutsRequestPromises.has(date)) {
    return breakoutsRequestPromises.get(date) ?? Promise.resolve([]);
  }

  const promise = retryWithBackoff(async () => {
    try {
      const data = await readJsonResponse<any[]>(getBreakoutsUrl(date));
      const normalized: BreakoutStockItem[] = Array.isArray(data)
        ? data
          .filter(Boolean)
          .map((item: any) => normalizeBreakoutStockItem(item, date, false))
          .filter((item) => Boolean(item.StockName))
        : [];
      if (normalized.length > 0) {
        breakoutsCache.set(date, normalized);
        return normalized;
      }
    } catch {
      // Split datewise file 404s (e.g. pre-Sept 11) -> fall back to consolidated analytics detail
    }

    try {
      const detail = await fetchAnalyticsDetail(date, options);
      const fallback = detail?.BreakoutStocks ?? [];
      breakoutsCache.set(date, fallback);
      return fallback;
    } catch {
      return [];
    }
  });

  breakoutsRequestPromises.set(date, promise);
  try {
    return await promise;
  } finally {
    breakoutsRequestPromises.delete(date);
  }
};

export const fetchApproachingBreakoutsDetail = async (
  date: string,
  options?: { forceRefresh?: boolean }
): Promise<BreakoutStockItem[]> => {
  const forceRefresh = options?.forceRefresh ?? false;
  if (!date) return [];
  if (!forceRefresh && approachingBreakoutsCache.has(date)) {
    return approachingBreakoutsCache.get(date) ?? [];
  }
  if (!forceRefresh && approachingBreakoutsRequestPromises.has(date)) {
    return approachingBreakoutsRequestPromises.get(date) ?? Promise.resolve([]);
  }

  const promise = retryWithBackoff(async () => {
    try {
      const data = await readJsonResponse<any[]>(getApproachingBreakoutsUrl(date));
      const normalized: BreakoutStockItem[] = Array.isArray(data)
        ? data
          .filter(Boolean)
          .map((item: any) => normalizeBreakoutStockItem(item, date, true))
          .filter((item) => Boolean(item.StockName))
        : [];
      if (normalized.length > 0) {
        approachingBreakoutsCache.set(date, normalized);
        return normalized;
      }
    } catch {
      // Split datewise file 404s (e.g. pre-Sept 11) -> fall back to consolidated analytics detail
    }

    try {
      const detail = await fetchAnalyticsDetail(date, options);
      const fallback = detail?.ApproachingBreakoutStocks ?? [];
      approachingBreakoutsCache.set(date, fallback);
      return fallback;
    } catch {
      return [];
    }
  });

  approachingBreakoutsRequestPromises.set(date, promise);
  try {
    return await promise;
  } finally {
    approachingBreakoutsRequestPromises.delete(date);
  }
};

const hydrateDetailFromSummary = (detail: Analytics, targetDate: string): Analytics => {
  const summaryItem = (analyticsCache ?? getCachedAnalyticsData())?.find((item) => item.Date === targetDate);

  const signalRows = Object.values(detail.SectorStockSignals ?? {}).flatMap((rows) => rows ?? []);
  const breakoutSignals = signalRows
    .filter((r) => r?.IsBreakout && r?.Symbol)
    .map((r) => normalizeBreakoutStockItem(r.Symbol, targetDate, false));

  const accumulatedSignals = signalRows
    .filter((r) => r?.IsAccumulated && r?.Symbol)
    .map((r) => r.Symbol);
  const dailyAcc = Object.values(detail.accumulationCount ?? {}).flatMap((s) => s ?? []);

  const mergedAccumulated = Array.from(
    new Set([
      ...(detail.accumulatedStocks ?? []),
      ...(summaryItem?.accumulatedStocks ?? []),
      ...accumulatedSignals,
      ...dailyAcc,
    ].filter(Boolean))
  );

  let mergedBreakouts = detail.BreakoutStocks && detail.BreakoutStocks.length > 0
    ? detail.BreakoutStocks
    : (summaryItem?.BreakoutStocks ?? []);

  if (mergedBreakouts.length === 0 && breakoutSignals.length > 0) {
    mergedBreakouts = breakoutSignals;
  } else if (breakoutSignals.length > 0) {
    // Add any missing breakout signals not already in mergedBreakouts
    const existingSet = new Set(mergedBreakouts.map((b) => b.StockName));
    const newItems = breakoutSignals.filter((b) => !existingSet.has(b.StockName));
    if (newItems.length > 0) {
      mergedBreakouts = [...mergedBreakouts, ...newItems];
    }
  }

  return {
    ...detail,
    BreakoutStocks: mergedBreakouts,
    BreakoutStockCount: detail.BreakoutStockCount || summaryItem?.BreakoutStockCount || mergedBreakouts.length,
    ApproachingBreakoutStocks:
      detail.ApproachingBreakoutStocks && detail.ApproachingBreakoutStocks.length > 0
        ? detail.ApproachingBreakoutStocks
        : (summaryItem?.ApproachingBreakoutStocks ?? []),
    ApproachingBreakoutCount: detail.ApproachingBreakoutCount || summaryItem?.ApproachingBreakoutCount || 0,
    VolumeShockers:
      detail.VolumeShockers && detail.VolumeShockers.length > 0
        ? detail.VolumeShockers
        : (summaryItem?.VolumeShockers ?? []),
    VolumeShockerCount: detail.VolumeShockerCount || summaryItem?.VolumeShockerCount || 0,
    OverSold:
      detail.OverSold && detail.OverSold.length > 0
        ? detail.OverSold
        : (summaryItem?.OverSold ?? []),
    OverSoldCount: detail.OverSoldCount || summaryItem?.OverSoldCount || 0,
    Overbought:
      detail.Overbought && detail.Overbought.length > 0
        ? detail.Overbought
        : (summaryItem?.Overbought ?? []),
    OverboughtCount: detail.OverboughtCount || summaryItem?.OverboughtCount || 0,
    accumulatedStocks: mergedAccumulated,
    FourPerPositiveClose:
      (detail.FourPerPositiveClose && detail.FourPerPositiveClose > 0)
        ? detail.FourPerPositiveClose
        : (summaryItem?.FourPerPositiveClose ?? detail.FourPerPositiveClose ?? 0),
    FourPerNegativeClose:
      (detail.FourPerNegativeClose && detail.FourPerNegativeClose > 0)
        ? detail.FourPerNegativeClose
        : (summaryItem?.FourPerNegativeClose ?? detail.FourPerNegativeClose ?? 0),
    PositiveClose:
      (detail.PositiveClose && detail.PositiveClose > 0)
        ? detail.PositiveClose
        : (summaryItem?.PositiveClose ?? detail.PositiveClose ?? 0),
    NegativeClose:
      (detail.NegativeClose && detail.NegativeClose > 0)
        ? detail.NegativeClose
        : (summaryItem?.NegativeClose ?? detail.NegativeClose ?? 0),
    lstAbvAllEma:
      detail.lstAbvAllEma && detail.lstAbvAllEma.length > 0
        ? detail.lstAbvAllEma
        : (summaryItem?.lstAbvAllEma ?? []),
    lstAbv21Ema:
      detail.lstAbv21Ema && detail.lstAbv21Ema.length > 0
        ? detail.lstAbv21Ema
        : (summaryItem?.lstAbv21Ema ?? []),
    Ema21_50_200_Stocks:
      detail.Ema21_50_200_Stocks && detail.Ema21_50_200_Stocks.length > 0
        ? detail.Ema21_50_200_Stocks
        : (summaryItem?.Ema21_50_200_Stocks ?? []),
    Ema21_50_200_Count:
      (detail.Ema21_50_200_Count && detail.Ema21_50_200_Count > 0)
        ? detail.Ema21_50_200_Count
        : (detail.Ema21_50_200_Stocks?.length || summaryItem?.Ema21_50_200_Count || 0),
  };
};

export const fetchSectorStockSignals = async (
  date: string,
  options?: { forceRefresh?: boolean }
): Promise<{ [sector: string]: SectorStockSignal[] } | null> => {
  const forceRefresh = options?.forceRefresh ?? false;

  if (!date) {
    return null;
  }

  const token = await getAuthToken();
  if (!token) {
    return null;
  }

  if (!forceRefresh && sectorStockSignalsCache.has(date)) {
    return sectorStockSignalsCache.get(date) ?? null;
  }

  if (!forceRefresh && sectorStockSignalsRequestPromises.has(date)) {
    return sectorStockSignalsRequestPromises.get(date) ?? Promise.resolve(null);
  }

  const requestPromise = retryWithBackoff(async () => {
    try {
      const url = `${API_CONFIG.BASE_URL}${API_CONFIG.ENDPOINTS.SECTOR_STOCK_SIGNALS(date)}`;
      const result = await readJsonResponse<{ [sector: string]: SectorStockSignal[] }>(url);
      if (result) {
        sectorStockSignalsCache.set(date, result);
        return result;
      }
      return null;
    } catch {
      return null;
    }
  });

  sectorStockSignalsRequestPromises.set(date, requestPromise);

  try {
    return await requestPromise;
  } finally {
    sectorStockSignalsRequestPromises.delete(date);
  }
};

export const fetchAnalyticsDetail = async (
  date: string,
  options?: { forceRefresh?: boolean; includeSectorSignals?: boolean }
): Promise<Analytics | null> => {
  const forceRefresh = options?.forceRefresh ?? false;
  const includeSectorSignals = options?.includeSectorSignals ?? false;

  if (!date) {
    return null;
  }

  // When user is not logged in, we should not even make a call to /api/data/analytics/:date
  const token = await getAuthToken();
  if (!token) {
    return null;
  }

  if (!forceRefresh && analyticsDetailCache.has(date)) {
    const cached = analyticsDetailCache.get(date) ?? null;
    if (cached && includeSectorSignals && (!cached.SectorStockSignals || Object.keys(cached.SectorStockSignals).length === 0)) {
      const signals = await fetchSectorStockSignals(date, { forceRefresh });
      if (signals) {
        cached.SectorStockSignals = signals;
      }
    }
    return cached;
  }

  if (!forceRefresh && analyticsDetailRequestPromises.has(date)) {
    const existingPromise = analyticsDetailRequestPromises.get(date) ?? Promise.resolve(null);
    const resolved = await existingPromise;
    if (resolved && includeSectorSignals && (!resolved.SectorStockSignals || Object.keys(resolved.SectorStockSignals).length === 0)) {
      const signals = await fetchSectorStockSignals(date, { forceRefresh });
      if (signals) {
        resolved.SectorStockSignals = signals;
      }
    }
    return resolved;
  }

  const requestPromise = retryWithBackoff(async () => {
    try {
      const detailResult = await readJsonResponse<Analytics>(getAnalyticsDetailUrl(date));
      const detail = hydrateDetailFromSummary(normalizeAnalytics(detailResult), date);

      if (includeSectorSignals && (!detail.SectorStockSignals || Object.keys(detail.SectorStockSignals).length === 0)) {
        const signals = await fetchSectorStockSignals(date, { forceRefresh });
        if (signals) {
          detail.SectorStockSignals = signals;
        }
      }

      analyticsDetailCache.set(date, detail);
      return detail;
    } catch (error) {
      console.warn(`Failed to load analytics detail for ${date}:`, error);
      const fallback =
        analyticsCache?.find((item) => item.Date === date) ||
        readPersistentAnalyticsCache()?.find((item) => item.Date === date);
      if (fallback) {
        const hydrated = hydrateDetailFromSummary(fallback, date);
        analyticsDetailCache.set(date, hydrated);
        return hydrated;
      }

      return null;
    }
  });

  analyticsDetailRequestPromises.set(date, requestPromise);

  try {
    return await requestPromise;
  } finally {
    analyticsDetailRequestPromises.delete(date);
  }
};

export const fetchVolumeStreaks = async (): Promise<VolumeAboveAverageStreak[]> => {
  const token = await getAuthToken();
  if (!token) {
    return [];
  }

  if (volumeStreaksCache) {
    return volumeStreaksCache;
  }

  if (volumeStreaksRequestPromise) {
    return volumeStreaksRequestPromise;
  }

  volumeStreaksRequestPromise = retryWithBackoff(async () => {
    try {
      const results = await readJsonResponse<VolumeAboveAverageStreak[]>(getVolumeStreaksUrl());
      const normalized = Array.isArray(results)
        ? results
          .filter((item): item is VolumeAboveAverageStreak => Boolean(item?.Symbol))
          .map((item) => ({
            Symbol: item.Symbol,
            ConsecutiveDays: Number(item.ConsecutiveDays ?? 0),
            LatestVolume: Number(item.LatestVolume ?? 0),
            LatestAverageVolume: item.LatestAverageVolume == null ? null : Number(item.LatestAverageVolume),
            Multiplier: item.Multiplier == null ? null : Number(item.Multiplier),
            DeliveryPercentage: Number(item.DeliveryPercentage ?? 0),
          }))
        : [];
      volumeStreaksCache = normalized;
      return normalized;
    } catch (err) {
      console.warn('Failed to load volume streaks:', err);
      volumeStreaksCache = [];
      return [];
    }
  });

  try {
    return await volumeStreaksRequestPromise;
  } finally {
    volumeStreaksRequestPromise = null;
  }
};

export const fetchPriceMovers = async (): Promise<ThirtyDayPriceMover[]> => {
  const token = await getAuthToken();
  if (!token) {
    return [];
  }

  if (priceMoversCache) {
    return priceMoversCache;
  }

  if (priceMoversRequestPromise) {
    return priceMoversRequestPromise;
  }

  priceMoversRequestPromise = retryWithBackoff(async () => {
    try {
      const results = await readJsonResponse<ThirtyDayPriceMover[]>(getPriceMoversUrl());
      const normalized = Array.isArray(results)
        ? results
          .filter((item): item is ThirtyDayPriceMover => Boolean(item?.Symbol))
          .map((item) => ({
            Symbol: item.Symbol,
            LowestCloseDate: item.LowestCloseDate ?? '',
            LowestClosePrice: Number(item.LowestClosePrice ?? 0),
            CurrentClosePrice: Number(item.CurrentClosePrice ?? 0),
            PercentageIncrease: Number(item.PercentageIncrease ?? 0),
          }))
        : [];
      priceMoversCache = normalized;
      return normalized;
    } catch (err) {
      console.warn('Failed to load 30-day price movers:', err);
      priceMoversCache = [];
      return [];
    }
  });

  try {
    return await priceMoversRequestPromise;
  } finally {
    priceMoversRequestPromise = null;
  }
};

const mergeAnalyticsCollections = (primary: Analytics[], secondary: Analytics[]): Analytics[] => {
  const map = new Map<string, Analytics>();
  for (const item of secondary) {
    if (item.Date) {
      map.set(item.Date, item);
    }
  }
  for (const item of primary) {
    if (item.Date) {
      const existing = map.get(item.Date);
      map.set(item.Date, existing ? { ...existing, ...item } : item);
    }
  }
  return sortAnalyticsData(Array.from(map.values()));
};

let archiveRequestPromise: Promise<Analytics[]> | null = null;
let isArchiveMerged = false;
let sectorSummaryCache: SectorSummary[] | null = null;
let sectorSummaryRequestPromise: Promise<SectorSummary[]> | null = null;

export const fetchAnalyticsArchive = async (): Promise<Analytics[]> => {
  if (archiveRequestPromise) {
    return archiveRequestPromise;
  }

  archiveRequestPromise = retryWithBackoff(async () => {
    try {
      console.log('Fetching analytics summary archive from:', buildDailyFreshUrl(analyticsSummaryArchiveUrl));
      const raw = await readJsonResponse<Analytics[]>(analyticsSummaryArchiveUrl, { dailyCache: true });
      const parsed = normalizeAnalyticsCollection(raw);
      console.log('=== Analytics Summary Archive Response ===');
      console.log('Total archive records:', parsed.length);
      return parsed;
    } catch (error) {
      console.warn('Failed to load analytics summary archive, falling back to legacy analytics:', error);
      return fetchLegacyAnalytics();
    }
  });

  try {
    return await archiveRequestPromise;
  } finally {
    archiveRequestPromise = null;
  }
};

export const fetchFullAnalyticsHistory = async (): Promise<Analytics[]> => {
  if (isArchiveMerged && analyticsCache && analyticsCache.length > 300) {
    return [...analyticsCache];
  }

  const archive = await fetchAnalyticsArchive();
  const current = analyticsCache ?? [];
  const merged = mergeAnalyticsCollections(current, archive);

  analyticsCache = merged;
  isArchiveMerged = true;
  writePersistentAnalyticsCache(merged);

  return [...merged];
};

export const isFullAnalyticsHistoryLoaded = (): boolean =>
  isArchiveMerged || (analyticsCache !== null && analyticsCache.length > 300);

const normalizeSectorSummaryItem = (item: SectorSummary): SectorSummary => ({
  ...item,
  SectorTotalStocks: mergeSectorNumberMap(item.SectorTotalStocks),
  SectorPositiveCloseCount: mergeSectorNumberMap(item.SectorPositiveCloseCount),
  SectorAbove21Ema: mergeSectorNumberMap(item.SectorAbove21Ema),
  SectorAccumulatedCount: mergeSectorNumberMap(item.SectorAccumulatedCount),
  SectorDeliveredValueCrores: mergeSectorNumberMap(item.SectorDeliveredValueCrores),
  SectorClosing52WeekHighCount: mergeSectorNumberMap(item.SectorClosing52WeekHighCount),
  SectorClosing52WeekLowCount: mergeSectorNumberMap(item.SectorClosing52WeekLowCount),
});

export const fetchSectorSummary = async (): Promise<SectorSummary[]> => {
  if (sectorSummaryCache) {
    return sectorSummaryCache;
  }

  if (sectorSummaryRequestPromise) {
    return sectorSummaryRequestPromise;
  }

  sectorSummaryRequestPromise = retryWithBackoff(async () => {
    try {
      console.log('Fetching sector summary from:', sectorSummaryUrl);
      const raw = await readJsonResponse<SectorSummary[]>(sectorSummaryUrl);
      const data = (Array.isArray(raw) ? raw : []).map(normalizeSectorSummaryItem);
      sectorSummaryCache = data;
      return data;
    } catch (error) {
      console.warn('Failed to load sector summary:', error);
      sectorSummaryCache = [];
      return [];
    }
  });

  try {
    return await sectorSummaryRequestPromise;
  } finally {
    sectorSummaryRequestPromise = null;
  }
};

/**
 * Loads full multi-year sector summary history (merging recent 90-day window with archive).
 */
export const fetchFullSectorSummary = async (): Promise<SectorSummary[]> => {
  const current = await fetchSectorSummary();
  if (isSectorArchiveMerged || current.length > 250) {
    return current;
  }

  if (sectorArchiveRequestPromise) {
    return sectorArchiveRequestPromise;
  }

  sectorArchiveRequestPromise = (async () => {
    try {
      const archiveRaw = await readJsonResponse<SectorSummary[]>(sectorSummaryArchiveUrl, { dailyCache: true });
      const archive = (Array.isArray(archiveRaw) ? archiveRaw : []).map(normalizeSectorSummaryItem);
      const dateMap = new Map<string, SectorSummary>();
      archive.forEach((item) => {
        if (item && item.Date) dateMap.set(item.Date, item);
      });
      current.forEach((item) => {
        if (item && item.Date) dateMap.set(item.Date, item);
      });
      const merged = Array.from(dateMap.values()).sort(
        (a, b) => new Date(a.Date).getTime() - new Date(b.Date).getTime()
      );
      sectorSummaryCache = merged;
      isSectorArchiveMerged = true;
      return merged;
    } catch (err) {
      console.warn('Failed to load sector summary archive, falling back to recent window:', err);
      return current;
    }
  })();

  try {
    return await sectorArchiveRequestPromise;
  } finally {
    sectorArchiveRequestPromise = null;
  }
};

/**
 * Loads the lightweight stock snapshot (prices, % change, volume, delivery, and 30D sparkline)
 * for all traded NSE stocks in a single batch request.
 */
export const fetchStocksSnapshot = async () => {
  return await ensureSnapshotLoaded();
};

export const fetchData = async (options?: { forceRefresh?: boolean }): Promise<Analytics[]> => {
  const forceRefresh = options?.forceRefresh ?? false;

  if (forceRefresh) {
    analyticsCache = null;
    analyticsRequestPromise = null;
    try {
      if (typeof window !== 'undefined' && window.localStorage) {
        window.localStorage.removeItem(ANALYTICS_STORAGE_KEY);
      }
    } catch {
      // Ignore storage errors in restricted contexts
    }
  }

  // Serve active in-memory session cache if already fetched during this session and not forced
  if (!forceRefresh && analyticsCache && analyticsCache.length > 0) {
    return [...analyticsCache];
  }

  if (!forceRefresh && analyticsRequestPromise) {
    return analyticsRequestPromise.then((data) => [...data]);
  }

  try {
    analyticsRequestPromise = retryWithBackoff(async () => {
      console.log('Fetching fresh analytics summary from:', analyticsSummaryUrl);

      const sorted = await fetchAnalyticsSummaryPayload({ fresh: forceRefresh });

      if (!forceRefresh && isArchiveMerged && analyticsCache && analyticsCache.length > sorted.length) {
        const merged = mergeAnalyticsCollections(sorted, analyticsCache);
        analyticsCache = merged;
        writePersistentAnalyticsCache(merged);
        return merged;
      }

      analyticsCache = sorted;
      writePersistentAnalyticsCache(sorted);

      return sorted;
    });

    const data = await analyticsRequestPromise;
    return [...data];
  } catch (error) {
    console.error('Error fetching analytics data, attempting cache fallback:', error);
    const persistentCache = readPersistentAnalyticsCache();
    if (persistentCache && persistentCache.length > 0) {
      analyticsCache = persistentCache;
      return [...persistentCache];
    }
    if (error instanceof Error) {
      throw new ApiError(`Failed to fetch from ${analyticsDataUrl}: ${error.message}`, undefined, analyticsDataUrl);
    }
    throw new ApiError(`Unexpected error fetching from ${analyticsDataUrl}`, undefined, analyticsDataUrl);
  } finally {
    analyticsRequestPromise = null;
  }
};

export const fetchSectorUniverse = async (options?: { forceRefresh?: boolean }): Promise<SectorUniverse> => {
  const forceRefresh = options?.forceRefresh ?? false;

  if (!forceRefresh && sectorUniverseCache) {
    return { ...sectorUniverseCache };
  }

  if (!forceRefresh && sectorUniverseRequestPromise) {
    return sectorUniverseRequestPromise.then((data) => ({ ...data }));
  }

  sectorUniverseRequestPromise = retryWithBackoff(async () => {
    try {
      const universe = await readJsonResponse<SectorUniverse>(sectorUniverseUrl);
      sectorUniverseCache = universe ?? {};
      return sectorUniverseCache;
    } catch (error) {
      console.warn('Failed to load sector universe:', error);
      sectorUniverseCache = {};
      return sectorUniverseCache;
    }
  });

  try {
    const data = await sectorUniverseRequestPromise;
    return { ...data };
  } finally {
    sectorUniverseRequestPromise = null;
  }
};

/**
 * Fetch market drawdown and 1-year return distribution data for broad breadth matrix.
 * Results are cached in-memory and in IndexedDB for the day.
 */
export const fetchMarketDrawdownDistribution = async (
  date?: string
): Promise<any | null> => {
  const cleanDate = date ? date.trim().slice(0, 10) : '';
  const cacheKey = cleanDate || '__latest__';

  if (marketDrawdownCache.has(cacheKey)) {
    return marketDrawdownCache.get(cacheKey);
  }
  if (marketDrawdownRequestPromises.has(cacheKey)) {
    return marketDrawdownRequestPromises.get(cacheKey)!;
  }

  const request = (async () => {
    const url = cleanDate ? getMarketDrawdownDailyUrl(cleanDate) : marketDrawdownLatestUrl;
    const staticFallbackUrl = cleanDate
      ? `/data/market-drawdown/${cleanDate}.json`
      : '/data/market-drawdown-distribution.json';

    try {
      const data = await readJsonResponse<any>(url, { dailyCache: true });
      if (data && (data.universes || data.Universes || data.all || data.nifty500)) {
        marketDrawdownCache.set(cacheKey, data);
        return data;
      }
    } catch {
      // Primary API endpoint failed or 404'd; try static public asset fallback next
    }

    try {
      const fallbackData = await readJsonResponse<any>(staticFallbackUrl, { dailyCache: true });
      if (
        fallbackData &&
        (fallbackData.universes || fallbackData.Universes || fallbackData.all || fallbackData.nifty500)
      ) {
        marketDrawdownCache.set(cacheKey, fallbackData);
        return fallbackData;
      }
    } catch (error) {
      console.warn(`Market drawdown distribution unavailable for ${cleanDate || 'latest'}, using fallback:`, error);
    }

    return null;
  })();

  marketDrawdownRequestPromises.set(cacheKey, request);
  try {
    return await request;
  } finally {
    marketDrawdownRequestPromises.delete(cacheKey);
  }
};

/**
 * Fetch historical data for a specific stock symbol with retry logic
 * @param symbol - Stock symbol to fetch data for
 * @returns Promise resolving to stock history
 */
export const fetchHistoryData = async (symbol: string): Promise<StockHistory> => {
  const cleanSymbol = symbol.trim().toUpperCase();
  const cached = stockHistoryApiCache.get(cleanSymbol);
  if (cached) {
    return cached;
  }
  const inflight = stockHistoryRequestPromises.get(cleanSymbol);
  if (inflight) {
    return inflight;
  }

  const url = getHistoryDataUrl(cleanSymbol);
  const request = (async () => {
    try {
      const data = await retryWithBackoff(async () => {
        return await readJsonResponse<StockHistory>(url, { dailyCache: true });
      });
      stockHistoryApiCache.set(cleanSymbol, data);
      return data;
    } catch (error) {
      console.error('Error fetching history data:', error);
      return handleApiError(error, url);
    }
  })();

  stockHistoryRequestPromises.set(cleanSymbol, request);
  try {
    return await request;
  } finally {
    stockHistoryRequestPromises.delete(cleanSymbol);
  }
};

/**
 * Fetch list of all available stocks with retry logic
 * @returns Promise resolving to array of stock symbols
 */
export const fetchAllStockData = async (): Promise<string[]> => {
  if (allStocksCache && allStocksCache.length > 0) {
    return allStocksCache;
  }
  if (allStocksRequestPromise) {
    return allStocksRequestPromise;
  }

  allStocksRequestPromise = (async () => {
    try {
      const data = await retryWithBackoff(async () => {
        return await readJsonResponse<string[]>(allStocksDataUrl, { dailyCache: true });
      });
      if (Array.isArray(data) && data.length > 0) {
        allStocksCache = data;
      }
      return data;
    } catch (error) {
      console.error('Error fetching all stock data:', error);
      return handleApiError(error, allStocksDataUrl);
    }
  })();

  try {
    return await allStocksRequestPromise;
  } finally {
    allStocksRequestPromise = null;
  }
};

/**
 * Fetch historical data for an NSE index (e.g. "Nifty 50", "Nifty 500") with retry logic.
 * Results are cached in-memory per session/day, mirroring fetchHistoryData for stocks.
 * Returns an empty history (rather than throwing) on failure, since callers typically use
 * this to render a small, non-critical ticker widget.
 *
 * Prefer `fetchIndexLatestSnapshot` instead when only today's value is needed (e.g. a ticker) —
 * this downloads the full multi-hundred-day history for one index and is intended for detail
 * views/charts, not lightweight "latest value" widgets.
 */
export const fetchIndexHistory = async (indexName: string): Promise<IndexHistory> => {
  const fileSafeName = toIndexFileSafeName(indexName);

  const cached = indexHistoryCache.get(fileSafeName);
  if (cached) {
    return cached;
  }

  const inflight = indexHistoryRequestPromises.get(fileSafeName);
  if (inflight) {
    return inflight;
  }

  const url = getIndexHistoryUrl(fileSafeName);
  const request = (async () => {
    try {
      const res = await retryWithBackoff(async () => {
        return await readJsonResponse<IndexHistory>(url, { dailyCache: true });
      });
      if (res && Array.isArray(res.History) && res.History.length > 0) {
        return res;
      }
    } catch {
      // Fall through to daily snapshot aggregator fallback
    }

    try {
      const availableDates = await readJsonResponse<string[]>(
        `${API_CONFIG.BASE_URL}/indices/available-dates`,
        { dailyCache: true }
      ).catch(() => null);

      if (availableDates && Array.isArray(availableDates) && availableDates.length > 0) {
        const matchingEntries: IndexHistoryEntry[] = [];
        await Promise.all(
          availableDates.map(async (d) => {
            try {
              const quotes = await fetchIndexDailySnapshot(d);
              const q = quotes.find(
                (item) => item.IndexName.toLowerCase() === indexName.toLowerCase()
              );
              if (q) matchingEntries.push(q);
            } catch {
              // Ignore single day failure
            }
          })
        );

        matchingEntries.sort(
          (a, b) => new Date(a.Date).getTime() - new Date(b.Date).getTime()
        );

        if (matchingEntries.length > 0) {
          return { IndexName: indexName, History: matchingEntries };
        }
      }
    } catch {
      // Ignore fallback failure
    }

    return { IndexName: indexName, History: [] };
  })();

  indexHistoryRequestPromises.set(fileSafeName, request);
  try {
    const data = await request;
    indexHistoryCache.set(fileSafeName, data);
    return data;
  } finally {
    indexHistoryRequestPromises.delete(fileSafeName);
  }
};

/**
 * Fetch the latest value for every tracked NSE index (Nifty 50, Nifty 500, sectoral indices, ...)
 * from a single compact `indices-latest.json` file, instead of downloading each index's full
 * per-day history just to read its most recent entry. Use this for lightweight "current value"
 * widgets (e.g. a ticker); use `fetchIndexHistory` only when a full time series is needed.
 */
export const fetchIndexLatestSnapshot = async (
  options?: { forceRefresh?: boolean }
): Promise<IndexLatestQuote[]> => {
  const forceRefresh = options?.forceRefresh ?? false;

  if (!forceRefresh && indexLatestCache) {
    return indexLatestCache;
  }

  if (!forceRefresh && indexLatestRequestPromise) {
    return indexLatestRequestPromise;
  }

  indexLatestRequestPromise = (async () => {
    try {
      return await retryWithBackoff(async () => {
        const data = await readJsonResponse<IndexLatestQuote[]>(
          indexLatestUrl,
          { fresh: forceRefresh, dailyCache: !forceRefresh }
        );
        return Array.isArray(data) ? data : [];
      });
    } catch (error) {
      console.warn('Failed to fetch latest index snapshot:', error);
      return [];
    }
  })();

  try {
    const data = await indexLatestRequestPromise;
    indexLatestCache = data;
    return data;
  } finally {
    indexLatestRequestPromise = null;
  }
};

/**
 * Fetch snapshot of all tracked NSE indices for a specific historical trading date.
 * Tries the API endpoint `/api/data/indices/daily/${date}` first, with fallback to
 * `/data/indices/daily/${date}.json`.
 */
export const fetchIndexDailySnapshot = async (
  date: string,
  options?: { forceRefresh?: boolean }
): Promise<IndexLatestQuote[]> => {
  if (!date) return [];
  const forceRefresh = options?.forceRefresh ?? false;
  if (!forceRefresh && indexDailyCache.has(date)) {
    return indexDailyCache.get(date) ?? [];
  }
  if (!forceRefresh && indexDailyRequestPromises.has(date)) {
    return indexDailyRequestPromises.get(date) ?? Promise.resolve([]);
  }

  const promise = retryWithBackoff(async () => {
    try {
      const data = await readJsonResponse<IndexLatestQuote[]>(getIndexDailyUrl(date), { dailyCache: true });
      if (Array.isArray(data) && data.length > 0) {
        indexDailyCache.set(date, data);
        return data;
      }
    } catch (proxyError) {
      console.warn(`Proxy fetch failed for index daily snapshot ${date}, trying static fallback:`, proxyError);
    }

    try {
      const fallbackUrl = `/data/indices/daily/${encodeURIComponent(date)}.json`;
      const fallbackRes = await fetch(fallbackUrl);
      if (fallbackRes.ok) {
        const fallbackData = await fallbackRes.json();
        if (Array.isArray(fallbackData) && fallbackData.length > 0) {
          indexDailyCache.set(date, fallbackData);
          return fallbackData;
        }
      }
    } catch (fallbackError) {
      console.warn(`Static fallback failed for index daily snapshot ${date}:`, fallbackError);
    }

    return [];
  });

  indexDailyRequestPromises.set(date, promise);
  try {
    return await promise;
  } finally {
    indexDailyRequestPromises.delete(date);
  }
};

/**
 * Fetch list of all historical trading dates available for NSE index snapshots.
 * Pulls from `/api/data/indices/available-dates` (proxied to Azure Blob Storage)
 * with graceful fallback to `/data/indices/available-dates.json`.
 */
export const fetchIndexAvailableDates = async (options?: { forceRefresh?: boolean }): Promise<string[]> => {
  const forceRefresh = options?.forceRefresh ?? false;
  if (!forceRefresh && indexAvailableDatesCache && indexAvailableDatesCache.length > 0) {
    return indexAvailableDatesCache;
  }
  if (!forceRefresh && indexAvailableDatesPromise) {
    return indexAvailableDatesPromise;
  }

  indexAvailableDatesPromise = (async () => {
    try {
      const data = await readJsonResponse<string[]>(
        `${API_CONFIG.BASE_URL}/indices/available-dates`,
        forceRefresh ? { fresh: true } : { dailyCache: true }
      );
      if (Array.isArray(data) && data.length > 0) {
        indexAvailableDatesCache = data;
        return data;
      }
    } catch (proxyError) {
      console.warn('Proxy fetch failed for index available-dates, trying static fallback:', proxyError);
    }

    try {
      const fallbackRes = await fetch('/data/indices/available-dates.json');
      if (fallbackRes.ok) {
        const fallbackData = await fallbackRes.json();
        if (Array.isArray(fallbackData) && fallbackData.length > 0) {
          indexAvailableDatesCache = fallbackData;
          return fallbackData;
        }
      }
    } catch (fallbackError) {
      console.warn('Static fallback failed for index available-dates:', fallbackError);
    }

    return [];
  })();

  try {
    return await indexAvailableDatesPromise;
  } finally {
    indexAvailableDatesPromise = null;
  }
};

/**
 * Fetch the latest real-time corporate RSS feed items (Announcements, Board Meetings, Corporate Actions, Insider Trading).
 * Ingested and categorized by NiftySutra.NSE.FeedWatcher every hour.
 */
export const fetchCorporateFeedsLatest = async (options: { fresh?: boolean } = {}): Promise<CorporateFeedItem[]> => {
  const isFresh = options.fresh ?? false;
  if (
    !isFresh &&
    corporateFeedsLatestCache &&
    Date.now() - corporateFeedsLatestCache.timestamp < CORPORATE_FEEDS_MEMORY_TTL_MS
  ) {
    return corporateFeedsLatestCache.data;
  }
  if (!isFresh && corporateFeedsLatestPromise) {
    return corporateFeedsLatestPromise;
  }

  const request = retryWithBackoff(async () => {
    try {
      const data = await readJsonResponse<CorporateFeedItem[]>(corporateFeedsLatestUrl, { fresh: isFresh });
      if (Array.isArray(data)) {
        corporateFeedsLatestCache = { data, timestamp: Date.now() };
        return data;
      }
    } catch (proxyError: any) {
      if (proxyError instanceof ApiError && proxyError.statusCode === 404) {
        return corporateFeedsLatestCache?.data ?? [];
      }
      console.warn('Proxy fetch failed for latest corporate feeds:', proxyError);
    }

    return corporateFeedsLatestCache?.data ?? [];
  });

  if (!isFresh) {
    corporateFeedsLatestPromise = request;
  }

  try {
    return await request;
  } finally {
    if (!isFresh) {
      corporateFeedsLatestPromise = null;
    }
  }
};

/**
 * Fetch run metadata and processing statistics of the corporate feed watcher.
 */
export const fetchCorporateFeedsMetadata = async (options: { fresh?: boolean } = {}): Promise<FeedRunMetadata | null> => {
  const isFresh = options.fresh ?? false;
  if (
    !isFresh &&
    corporateFeedsMetadataCache &&
    Date.now() - corporateFeedsMetadataCache.timestamp < CORPORATE_FEEDS_MEMORY_TTL_MS
  ) {
    return corporateFeedsMetadataCache.data;
  }
  if (!isFresh && corporateFeedsMetadataPromise) {
    return corporateFeedsMetadataPromise;
  }

  const request = (async () => {
    try {
      return await retryWithBackoff(async () => {
        try {
          const data = await readJsonResponse<FeedRunMetadata>(corporateFeedsMetadataUrl, { fresh: isFresh });
          if (data && typeof data === 'object') {
            corporateFeedsMetadataCache = { data, timestamp: Date.now() };
            return data;
          }
        } catch (proxyError: any) {
          if (proxyError instanceof ApiError && proxyError.statusCode === 404) {
            return corporateFeedsMetadataCache?.data ?? null;
          }
          console.warn('Proxy fetch failed for corporate feeds metadata:', proxyError);
        }

        return corporateFeedsMetadataCache?.data ?? null;
      });
    } catch (error) {
      console.warn('Failed to fetch corporate feeds metadata:', error);
      return corporateFeedsMetadataCache?.data ?? null;
    }
  })();

  if (!isFresh) {
    corporateFeedsMetadataPromise = request;
  }

  try {
    return await request;
  } finally {
    if (!isFresh) {
      corporateFeedsMetadataPromise = null;
    }
  }
};

/**
 * Fetch historical archived corporate feeds for a specific date (yyyy-MM-dd).
 */
export const fetchCorporateFeedsDaily = async (date: string, options: { fresh?: boolean } = {}): Promise<CorporateFeedItem[]> => {
  if (!date) return [];
  const isFresh = options.fresh ?? false;

  if (!isFresh && corporateFeedsDailyCache.has(date)) {
    return corporateFeedsDailyCache.get(date)!;
  }
  if (!isFresh && corporateFeedsDailyPromises.has(date)) {
    return corporateFeedsDailyPromises.get(date)!;
  }

  const request = (async () => {
    try {
      return await retryWithBackoff(async () => {
        const url = getCorporateFeedsDailyUrl(date);
        try {
          const data = await readJsonResponse<CorporateFeedItem[]>(url, {
            dailyCache: !isFresh,
            fresh: isFresh,
          });
          if (Array.isArray(data)) {
            corporateFeedsDailyCache.set(date, data);
            return data;
          }
        } catch (proxyError: any) {
          if (proxyError instanceof ApiError && proxyError.statusCode === 404) {
            corporateFeedsDailyCache.set(date, []);
            return [];
          }
          console.warn(`Proxy fetch failed for daily corporate feeds (${date}):`, proxyError);
        }

        return corporateFeedsDailyCache.get(date) ?? [];
      });
    } catch (error) {
      console.warn(`Failed to fetch corporate feeds for date ${date}:`, error);
      return corporateFeedsDailyCache.get(date) ?? [];
    }
  })();

  if (!isFresh) {
    corporateFeedsDailyPromises.set(date, request);
  }

  try {
    return await request;
  } finally {
    if (!isFresh) {
      corporateFeedsDailyPromises.delete(date);
    }
  }
};

/**
 * Fetch filings and corporate feed items for a specific company / symbol (e.g. 'RELIANCE', 'LTF', 'ABB').
 * Retains the latest filings for that company even after being evicted from the market-wide latest feed.
 */
export const fetchCorporateFeedsByCompany = async (symbol: string, options: { fresh?: boolean } = {}): Promise<CorporateFeedItem[]> => {
  if (!symbol || !symbol.trim()) return [];
  const cleanSymbol = symbol.trim().toUpperCase().replace(/\.(NS|BO)$/i, '');
  const isFresh = options.fresh ?? false;

  if (!isFresh && corporateFeedsCompanyCache.has(cleanSymbol)) {
    return corporateFeedsCompanyCache.get(cleanSymbol)!;
  }
  if (!isFresh && corporateFeedsCompanyPromises.has(cleanSymbol)) {
    return corporateFeedsCompanyPromises.get(cleanSymbol)!;
  }

  const request = (async () => {
    try {
      return await retryWithBackoff(async () => {
        const url = getCorporateFeedsCompanyUrl(cleanSymbol);
        try {
          const data = await readJsonResponse<CorporateFeedItem[]>(url, { fresh: isFresh });
          if (Array.isArray(data)) {
            corporateFeedsCompanyCache.set(cleanSymbol, data);
            return data;
          }
        } catch (proxyError: any) {
          if (proxyError instanceof ApiError && proxyError.statusCode === 404) {
            corporateFeedsCompanyCache.set(cleanSymbol, []);
            return [];
          }
          console.warn(`Proxy fetch failed for company ${cleanSymbol}:`, proxyError);
        }

        return corporateFeedsCompanyCache.get(cleanSymbol) ?? [];
      });
    } catch (error) {
      console.warn(`Failed to fetch corporate feeds for company ${cleanSymbol}:`, error);
      return corporateFeedsCompanyCache.get(cleanSymbol) ?? [];
    }
  })();

  if (!isFresh) {
    corporateFeedsCompanyPromises.set(cleanSymbol, request);
  }

  try {
    return await request;
  } finally {
    if (!isFresh) {
      corporateFeedsCompanyPromises.delete(cleanSymbol);
    }
  }
};

/**
 * Fetch consolidated 180-day high-impact catalysts summary index (Preferential issues, Order wins, M&A, Capex).
 * Used across the application for high-impact catalyst badges and filing indicators with zero per-stock network overhead.
 */
export const fetchCorporateCatalystsSummary = async (options: { fresh?: boolean } = {}): Promise<CatalystsSummaryIndex | null> => {
  const isFresh = options.fresh ?? false;
  if (!isFresh && corporateCatalystsSummaryCache) {
    return corporateCatalystsSummaryCache;
  }
  if (!isFresh && corporateCatalystsSummaryPromise) {
    return corporateCatalystsSummaryPromise;
  }

  const request = (async () => {
    try {
      return await retryWithBackoff(async () => {
        const url = isFresh ? buildFreshUrl(corporateFeedsCatalystsSummaryUrl) : corporateFeedsCatalystsSummaryUrl;
        try {
          const data = await readJsonResponse<CatalystsSummaryIndex>(url, { fresh: isFresh });
          if (data && typeof data === 'object' && data.CatalystsBySymbol) {
            corporateCatalystsSummaryCache = data;
            return data;
          }
        } catch (proxyError: any) {
          if (proxyError instanceof ApiError && proxyError.statusCode === 404) {
            return corporateCatalystsSummaryCache;
          }
          console.warn('Proxy fetch failed for corporate catalysts summary:', proxyError);
        }

        return corporateCatalystsSummaryCache;
      });
    } catch (error) {
      console.warn('Failed to fetch corporate catalysts summary:', error);
      return corporateCatalystsSummaryCache;
    }
  })();

  if (!isFresh) {
    corporateCatalystsSummaryPromise = request;
  }

  try {
    return await request;
  } finally {
    if (!isFresh) {
      corporateCatalystsSummaryPromise = null;
    }
  }
};

/**
 * Fetch the latest execution and progress status of the Calculator WebJob.
 * Dual-resolves .dat and .json, automatically authenticated for Admin users.
 */
export const fetchCalculatorStatus = async (options: { fresh?: boolean } = {}): Promise<CalculatorStatus | null> => {
  if (calculatorStatusPromise) {
    return calculatorStatusPromise;
  }

  calculatorStatusPromise = (async () => {
    try {
      const raw = await readJsonResponse<CalculatorStatus>(calculatorStatusUrl, { fresh: options.fresh ?? true });
      if (!raw) return null;

      const status: CalculatorStatus = {
        ...raw,
        jobName: raw.jobName || raw.JobName || 'calculator',
        status: (raw.status || raw.Status || 'Idle') as 'Running' | 'Completed' | 'Failed' | 'Idle',
        runId: raw.runId || raw.RunId,
        startedAt: raw.startedAt || raw.StartedAt,
        completedAt: raw.completedAt || raw.CompletedAt,
        lastHeartbeat: raw.lastHeartbeat || raw.LastHeartbeat,
        runDate: raw.runDate || raw.RunDate,
        totalStocks: raw.totalStocks ?? raw.TotalStocks ?? 0,
        processedStocks: raw.processedStocks ?? raw.ProcessedStocks ?? 0,
        percentComplete: raw.percentComplete ?? raw.PercentComplete ?? 0,
        durationSeconds: raw.durationSeconds ?? raw.DurationSeconds ?? 0,
        processingRate: raw.processingRate ?? raw.ProcessingRate ?? 0,
        phase: raw.phase || raw.Phase,
        errorMessage: raw.errorMessage || raw.ErrorMessage,
      };

      return status;
    } catch (error) {
      // Return null if status blob is not available yet or request failed
      return null;
    }
  })();

  try {
    return await calculatorStatusPromise;
  } finally {
    calculatorStatusPromise = null;
  }
};

